import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Dialog } from "@mui/material";
import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { partyApi } from "./apiService";
import { useToast } from "../../../components/Toast";
import { commonButtonProps } from "../../../utils/styles";
import { commonTextFieldProps } from "../../../utils/styles";

const validationSchema = Yup.object().shape({
  partyName: Yup.string().required("Party name is required"),
  primaryContactName: Yup.string().required("Primary contact name is required"),
  primaryContactNumber: Yup.string()
    .required("Primary contact number is required")
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
  email: Yup.string().email("Invalid email format").optional(),
  address: Yup.string().optional(),
});

const PartyActionModal = ({
  open,
  onClose,
  party,
  isEdit = false,
  user,
  setIsSubmitting,
  setParties,
  setIsLoading,
  setShouldFetchParties,
  isSubmitting,
}: any) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const { showToast } = useToast();

  const initialValues = {
    ...(party?.id && { id: party?.id }),
    partyName: party?.partyName || "",
    primaryContactName: party?.primaryContactName || "",
    primaryContactNumber: party?.primaryContactNumber || "",
    email: party?.email || "",
    address: party?.address || "",
    companyId: companyId,
  };

  const handleSubmit = (values: any) => {
    setIsSubmitting(true);
    if (isEdit) {
      partyApi
        .addEditParty(values, true)
        .then((res) => {
          if (res) {
            setShouldFetchParties(true);
            showToast("Party edited successfully", "success");
          }
        })
        .catch((error) => {
          if (error?.response?.status === 401) {
            showToast("Session expired, please login again", "error");
          } else {
            showToast("Party edit failed", "error");
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      partyApi
        .addEditParty(values, false)
        .then((res) => {
          if (res) {
            setShouldFetchParties(true);
            showToast("Party added successfully", "success");
          }
        })
        .catch((error) => {
          if (error?.response?.status === 401) {
            showToast("Session expired, please login again", "error");
          } else {
            showToast("Party add failed", "error");
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Party" : "Add Party"}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    name="partyName"
                    as={TextField}
                    label="Party Name"
                    fullWidth
                    error={touched?.partyName && Boolean(errors?.partyName)}
                    helperText={touched?.partyName && errors?.partyName}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name="primaryContactName"
                    as={TextField}
                    label="Primary Contact Name"
                    fullWidth
                    error={
                      touched?.primaryContactName &&
                      Boolean(errors?.primaryContactName)
                    }
                    helperText={
                      touched?.primaryContactName && errors?.primaryContactName
                    }
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name="primaryContactNumber"
                    as={TextField}
                    label="Primary Contact Number"
                    fullWidth
                    error={
                      touched?.primaryContactNumber &&
                      Boolean(errors?.primaryContactNumber)
                    }
                    helperText={
                      touched?.primaryContactNumber &&
                      errors?.primaryContactNumber
                    }
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 10,
                      pattern: "[0-9]*",
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name="email"
                    as={TextField}
                    label="Email (Optional)"
                    fullWidth
                    error={touched?.email && Boolean(errors?.email)}
                    helperText={touched?.email && errors?.email}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Field
                    name="address"
                    as={TextField}
                    label="Address (Optional)"
                    fullWidth
                    multiline
                    rows={2}
                    error={touched?.address && Boolean(errors?.address)}
                    helperText={touched?.address && errors?.address}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty)}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Add"}
                {isSubmitting && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default PartyActionModal;
